import { Deal } from "@app/types/ServerCustomContext";
import amazonPricingDataLoader, {
  extractAmazonLoader,
} from "@app/utils/amazonPricingDataloader";
import { GA4_REF_CODE_AP_WIDGET } from "@app/utils/ga/ref-code";
import { buildUrlWithGARefCode } from "@app/utils/ga/tracking";
import { formatDiscount, formatPrice } from "@app/utils/product";
import {
  configAffiliatesDeclaration,
  configAffiliatesProduct,
} from "@pub/config";
import placeholders from "@pub/placeholders";
import {
  AffiliateProduct,
  AffiliatesProductCarousell,
} from "@sphtech/dmg-design-system";
import { ResolvedDealsElement } from "@src/app/layouts/Article/elements";
import { getGeniusLink } from "@src/app/utils/trackers/skimlinks";
import { ReactNode } from "react";
import useSWR from "swr";

import Products from "./Products";

type AffiliatesProduct = React.ComponentProps<typeof AffiliateProduct>;
type Props = {
  data: {
    items: Deal[];
    itemHeight?: number;
    itemWidth?: number;
    noOfItemsInFrame?: number;
    navIcon?: {
      next: ReactNode;
      prev: ReactNode;
    };
    carouselWidthLeeWay?: number;
  };
  isCarousel?: boolean;
  sourceNameAsLabel?: boolean;
  options?: AffiliatesProduct["options"] & {
    withPriceLabel?: boolean;
    imageCropType?:
      | "landscape"
      | "portrait"
      | "original"
      | "square"
      | "square_30_26";
  };
};

export default function ProductDeals({
  data,
  isCarousel = true,
  options = {
    withPriceLabel: true,
  },
}: Props): ReactNode {
  const {
    items,
    itemHeight,
    itemWidth,
    noOfItemsInFrame,
    carouselWidthLeeWay,
  } = data;

  const dyanmicPricing = items
    .map(
      (item) =>
        item.deals && item.deals[0]?.additionalFields?.dynamicPricing?.value,
      //Only get the first deal's with dynamic pricing
    )
    .filter(Boolean);

  const { data: amazonAffiatesData, isLoading } = useSWR(
    dyanmicPricing,
    (keys) => {
      return amazonPricingDataLoader
        .loadMany(keys)
        .then((data) => extractAmazonLoader(data));
    },
  );

  const mapProductItemDeals = (deals?: ResolvedDealsElement[]) => {
    if (!deals || deals.length === 0) return [];

    return deals.map((deal) => {
      const additionalFields = deal.additionalFields;
      const dynamicPricing = additionalFields?.dynamicPricing?.value;
      const extractAmazonData = dynamicPricing
        ? amazonAffiatesData?.find((item) => item.asin === dynamicPricing)
        : undefined;

      const originalPrice = formatPrice(
        extractAmazonData?.originalPrice || additionalFields?.price?.value,
      );
      const discountPrice = formatPrice(
        extractAmazonData?.discountPrice ||
          additionalFields?.discountedPrice?.value,
      );

      const destinationUrl = getGeniusLink(
        additionalFields?.destinationUrl?.value || "",
      );

      return {
        discountInPercentage:
          formatDiscount(
            extractAmazonData?.discountPercentage ||
              additionalFields?.discount?.value,
            true,
            false,
          ) || "",
        discountPrice: discountPrice || "",
        discountPriceLabel:
          discountPrice && options.withPriceLabel ? "Now" : "",
        link: buildUrlWithGARefCode(destinationUrl, GA4_REF_CODE_AP_WIDGET),
        originalPrice: originalPrice || "",
        originalPriceLabel:
          originalPrice && options.withPriceLabel
            ? configAffiliatesProduct.originalPriceLabel || "Original Price: "
            : "",
        dynamicPricing: additionalFields?.dynamicPricing?.value,
        sourceName: additionalFields?.sourceName?.value,
      };
    });
  };

  const productItems = items.map((item) => {
    const crop =
      item.image?.crops[
        options.imageCropType || configAffiliatesProduct.imageCrop || "original"
      ] || placeholders.portrait;
    const deals = mapProductItemDeals(item.deals);

    return {
      image: crop,
      title: item.title,
      dealsSource: deals,
      productInfo: {
        headerTitle: item.productInfo?.additionalFields.headerTitle?.value,
        discountText: item.productInfo?.additionalFields.discountText?.value,
      },
    };
  });

  return (
    !isLoading &&
    items.length > 0 &&
    (!isCarousel ? (
      <Products
        cta={{
          variant: configAffiliatesProduct.cta?.variant,
          buttonIcon: configAffiliatesProduct.cta?.buttonIcon || "",
          label: configAffiliatesProduct.cta?.label || "SHOP NOW",
        }}
        items={productItems}
        options={options}
      />
    ) : (
      <AffiliatesProductCarousell
        cta={{
          variant: configAffiliatesProduct.cta?.variant,
          buttonIcon: configAffiliatesProduct.cta?.buttonIcon || "",
          label: configAffiliatesProduct.cta?.label || "SHOP NOW",
        }}
        itemHeight={itemHeight || 400}
        itemWidth={configAffiliatesProduct.itemWidth || itemWidth || 190}
        items={productItems}
        noOfItemsInFrame={noOfItemsInFrame || 4}
        carouselWidthLeeWay={carouselWidthLeeWay}
        arrowSize={configAffiliatesProduct.arrowSize}
        arrowVariant={configAffiliatesProduct.arrowVariant}
        disclaimer={configAffiliatesDeclaration.disclaimer}
      />
    ))
  );
}
